import * as React from 'react'

import { Plus } from 'lucide-react'

import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import {
  GroupHeader,
  GroupHeaderTrigger,
} from '~/client/src/shared/components/GroupHeader'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

interface IBandHeaderProps {
  companyId: string
  companyName: string
  companyIconUrl: string
  roleTagsLabel: string
  memberCount: number
  expanded: boolean
  bandSelected: boolean
  bandIndeterminate: boolean
  onToggleSelection: () => void
  onToggleExpand: () => void
  onAddProjectMember: (companyId: string | null) => void
}

export default function BandHeader({
  companyId,
  companyName,
  companyIconUrl,
  roleTagsLabel,
  memberCount,
  expanded,
  bandSelected,
  bandIndeterminate,
  onToggleSelection,
  onToggleExpand,
  onAddProjectMember,
}: IBandHeaderProps): React.ReactElement {
  const isUnassigned = !companyId || companyId === UNASSIGNED

  const handleAddClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    onAddProjectMember(isUnassigned ? null : companyId)
  }

  const label = roleTagsLabel ? (
    <>
      {companyName}
      <span className="tw:ml-2 tw:text-muted-foreground">{roleTagsLabel}</span>
    </>
  ) : (
    companyName
  )

  return (
    // pl: row checkbox is centred in the 44px column's 43px padding box (1px border-r) → (43 - 16) / 2.
    <GroupHeader className="tw:sticky tw:left-0 tw:z-[1] tw:w-fit tw:max-w-full tw:border-b-0 tw:pl-[13.5px]">
      <Checkbox
        checked={bandSelected}
        indeterminate={bandIndeterminate}
        onCheckedChange={() => onToggleSelection()}
        aria-label={Localization.translator.select}
      />

      <GroupHeaderTrigger
        expanded={expanded}
        onClick={onToggleExpand}
        ariaLabel={companyName}
        label={label}
        labelIcon={
          !isUnassigned ? (
            <Avatar
              src={companyIconUrl || undefined}
              avatarType={AvatarType.Company}
              size={AvatarSize.Minuscule}
            />
          ) : undefined
        }
        count={memberCount}
      />

      {!isUnassigned && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={handleAddClick}
          aria-label={Localization.translator.addNewProjectMember}
          className="tw:opacity-0 tw:transition-opacity tw:group-hover:opacity-100 tw:focus-visible:opacity-100"
        >
          <Plus aria-hidden="true" />
        </Button>
      )}
    </GroupHeader>
  )
}
